/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  DigitalSignCanvas,
  DigitalSignCanvasRef,
} from '@euler/app/components/signature/DigitalSignCanvas';
import { Typography } from '@euler/app/components/typography';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useLandscapeOrientation } from '@euler/functions/useLandscapeOrientation';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import { AnimatePresence, MotiView } from 'moti';
import { FC, memo, useCallback, useRef, useState } from 'react';
import {
  Platform,
  TouchableOpacity,
  TouchableOpacityProps,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ViewShot from 'react-native-view-shot';
import { useTheme } from 'styled-components';

const kButtonSize = 56;

const FloatingButton: FC<TouchableOpacityProps> = memo(({ ...props }) => {
  const { rpx } = useResponsiveContext();
  const buttonSize = rpx(kButtonSize);
  return (
    <TouchableOpacity
      css={`
        width: ${buttonSize}px;
        height: ${buttonSize}px;
        border-radius: ${buttonSize / 2}px;
        box-shadow: 0 0 ${rpx(5)}px rgba(0, 0, 0, 0.3);
        elevation: 3;
        align-items: center;
        justify-content: center;
      `}
      {...props}
    />
  );
});

export const SignatureScreen = wrapNavigatorScreen(
  ({ onDone }: { onDone?: (result: string | undefined) => void }) => {
    useLandscapeOrientation();

    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const insets = useSafeAreaInsets();
    const navigation = useNavigation();
    const viewShotRef = useRef<ViewShot>(null);
    const canvasRef = useRef<DigitalSignCanvasRef>(null);
    const [hasDrawings, setHasDrawings] = useState(false);

    const onConfirm = useCallback(async () => {
      const uri = await viewShotRef.current?.capture?.();
      navigation.goBack();
      onDone?.(uri);
    }, [navigation, onDone]);

    const onReset = useCallback(() => {
      canvasRef.current?.clear();
    }, []);

    const onBack = useCallback(() => {
      navigation.goBack();
    }, [navigation]);

    return (
      <>
        <ViewShot
          ref={viewShotRef}
          options={{ format: 'jpg', quality: 0.9 }}
          css={`
            flex: 1;
          `}
        >
          <DigitalSignCanvas
            ref={canvasRef}
            css={`
              background-color: #fff;
            `}
            style={
              Platform.OS === 'web'
                ? { width: '100%', height: '100%' }
                : undefined
            }
            onChange={(data: string) => {
              setHasDrawings(Boolean(data));
            }}
          />
        </ViewShot>

        <AnimatePresence>
          <MotiView
            from={{ translateY: rpx(-55), opacity: 0 }}
            animate={{
              translateY: 0,
              opacity: 1,
            }}
            exit={{ translateY: rpx(-55), opacity: 0 }}
            css={`
              position: absolute;
              left: ${insets.left}px;
              top: ${insets.top}px;
              align-items: center;
              justify-content: center;
              padding: ${rpx(15)}px;
            `}
            pointerEvents="box-none"
          >
            <FloatingButton
              css={`
                background-color: transparent;
              `}
              onPress={onBack}
            >
              <Icon
                name="arrow-undo-circle-outline"
                size={rpx(28)}
                color="#333"
              />
            </FloatingButton>
          </MotiView>
        </AnimatePresence>

        <AnimatePresence>
          <MotiView
            from={{ translateY: rpx(55), opacity: 0 }}
            animate={{
              translateY: 0,
              opacity: 1,
            }}
            exit={{ translateY: rpx(55), opacity: 0 }}
            css={`
              position: absolute;
              left: ${insets.left}px;
              right: ${insets.right}px;
              bottom: ${insets.bottom}px;
              align-items: center;
              justify-content: space-between;
              flex-direction: row;
              padding: ${rpx(15)}px;
            `}
            pointerEvents="box-none"
          >
            <FloatingButton
              css={`
                background-color: #aaa;
              `}
              onPress={onReset}
            >
              <Typography.Label size={rpx(16)} light color="#333">
                重签
              </Typography.Label>
            </FloatingButton>

            <FloatingButton
              css={`
                background-color: ${theme.link};
                opacity: ${hasDrawings ? 1 : 0.5};
              `}
              disabled={!hasDrawings}
              onPress={onConfirm}
            >
              <Typography.Label size={rpx(16)} light color="#fff">
                完成
              </Typography.Label>
            </FloatingButton>
          </MotiView>
        </AnimatePresence>
      </>
    );
  },
  {
    headerShown: false,
  },
);
